import { type } from "os";
import React, { ReactNode } from "react";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useAnimate } from "framer-motion";

import { MOBILE } from "../../constants/breakpoints";

interface BtnProps {
  children: ReactNode;
  onClick: () => void;
}

function HeroButton({ children, onClick }: BtnProps) {
  return <Large onClick={onClick}>{children} </Large>;
}

const Large = ({ children, onClick }: BtnProps) => {
  let [scope, animate] = useAnimate();

  const startAnimation = () => {
    animate(
      "#mainshape",
      {
        x: 110,
        y: -10,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt1",
      {
        y: -92,
        scale: 1.01,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt2",
      {
        y: -95,
        x: -40,
        scale: 1.07,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt3",
      {
        y: 103,
        scale: 1.03,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#distortion",
      {
        x: -290,
        y: 50,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#text",
      {
        rotate: -2.78,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
  };

  const exitAnimation = () => {
    animate(
      "#mainshape",
      {
        x: 0,
        y: 0,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt1",
      {
        y: 0,
        scale: 1,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt2",
      {
        y: 0,
        x: 0,
        scale: 1,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#pt3",
      {
        y: 0,
        scale: 1,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#distortion",
      {
        x: 0,
        y: 0,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
    animate(
      "#text",
      {
        rotate: 0,
      },
      {
        duration: 0.6,
        ease: "backOut",
      }
    );
  };

  return (
    <div
      onClick={onClick}
      ref={scope}
      css={css`
        display: flex;
        width: 502px;
        height: 284px;
        background: #ffffff;
        position: relative;
        overflow: hidden;
        margin-left: -11%;
        margin-top: -8%;
        justify-content: center;
        align-items: center;

        @media (max-width: ${MOBILE}px) {
          display: none;
        }
      `}
    >
      <BlurGroup />
      <Effects />
      <p
        id="text"
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          width: 363px;
          height: 88px;
          font-style: italic;
          font-weight: 800;
          font-size: 24px;
          position: absolute;
          right: 0;
          left: 0;
          top: 0;
          bottom: 0;
          margin: auto auto;
          text-align: center;
          cursor: pointer;
        `}
        onMouseEnter={(event) => {
          startAnimation();
        }}
        onMouseLeave={() => {
          exitAnimation();
        }}
      >
        {children}
      </p>
    </div>
  );

  function BlurGroup() {
    return (
      <div
        id="blur-group"
        css={css`
          display: flex;
          width: 100%;
          height: 100%;
          position: relative;
          filter: blur(25px);
        `}
      >
        <div
          id="btn"
          css={css`
            display: flex;
            width: 363px;
            height: 88px;
            position: absolute;
            background: #000000;
            right: 0;
            left: 0;
            top: 0;
            bottom: 0;
            margin: auto auto;
            text-align: center;
          `}
        ></div>
        <div
          id="distortion"
          css={css`
            display: flex;
            width: 270.8px;
            height: 270.8px;
            position: absolute;
            bottom: 0;
            top: 10%;
            right: -15%;
            margin: auto 0;
            mix-blend-mode: exclusion;
            border: 10px solid #aeaeae;
            transform: matrix(0.89, -0.45, -0.45, -0.89, 0, 0);
          `}
        ></div>
        <div
          css={css`
            display: flex;
            width: 96.15px;
            height: 96.15px;
            position: absolute;
            transform: rotate(95.71deg);
            bottom: 0;
            top: 0;
            left: 25%;
            margin: auto 0;
            background: #000000;
          `}
          id="mainshape"
        ></div>
        <div
          id="pt1"
          css={css`
            background: #000000;
            transform: rotate(95.71deg);
            display: flex;
            width: 54px;
            height: 54px;
            position: absolute;
            left: 75%;
            top: 40%;
          `}
        ></div>
        <div
          id="pt2"
          css={css`
            background: #000000;
            transform: rotate(95.71deg);
            display: flex;
            width: 54px;
            height: 54px;
            position: absolute;
            right: 75%;
            top: 40%;
          `}
        ></div>
        <div
          id="pt3"
          css={css`
            background: #000000;
            transform: rotate(95.71deg);
            display: flex;
            width: 54px;
            height: 54px;
            position: absolute;
            left: 60%;
            top: 40%;
          `}
        ></div>
      </div>
    );
  }

  function Effects() {
    return (
      <div
        id="effects"
        css={css`
          display: flex;
          width: 800px;
          height: 346px;
          position: absolute;
        `}
      >
        {/* Low-pass filter */}
        <div
          id="low-pass"
          css={css`
            background: #878787;
            mix-blend-mode: color-dodge;
            position: absolute;
            display: flex;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
          `}
        ></div>
        {/* Sharpness */}
        <div
          id="sharpness"
          css={css`
            background: #000000;
            mix-blend-mode: color-burn;
            position: absolute;
            display: flex;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
          `}
        ></div>
        {/* Color */}
        <div
          id="color"
          css={css`
            mix-blend-mode: screen;
            position: absolute;
            display: flex;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
          `}
        >
          {/* Content */}
          <div
            id="cotent"
            css={css`
              display: flex;
              width: 800px;
              height: 346px;
              background: #ffd550;
              display: flex;
              width: 100%;
              height: 100%;
              left: 0;
              top: 0;
            `}
          ></div>
        </div>
      </div>
    );
  }
};

export default HeroButton;
